import { useRef } from 'react';
import {
  Avatar,
  Button,
  Card,
  Chip,
  Stack,
  Typography,
} from '@locus-taxy/encore-mui';
import { Person } from '@locus-taxy/encore-mui/icons';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { getTestId } from '@locus-taxy/encore-utils';
import { useToast } from '@locus-taxy/encore-ui-toolkit';
import { TestIdAction } from '../../utils/testIdUtils';
import type { ProfileViewMode } from './profileTypes';
import { PHOTO_MAX_SIZE_BYTES, PHOTO_ACCEPTED_TYPES } from './profileConstants';

interface ProfileHeaderCardProps {
  readonly mode: ProfileViewMode;
  readonly fullName: string;
  readonly designation?: string;
  readonly roleName?: string;
  readonly photoUrl?: string | null;
  readonly photoLoading?: boolean;
  readonly onPhotoSelect?: (file: File) => void;
  readonly onPhotoRemove?: () => void;
  readonly hasPhoto?: boolean;
}

export function ProfileHeaderCard({
  mode,
  fullName,
  designation,
  roleName,
  photoUrl,
  photoLoading,
  onPhotoSelect,
  onPhotoRemove,
  hasPhoto,
}: ProfileHeaderCardProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showError } = useToast();

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) { return; }

    if (!PHOTO_ACCEPTED_TYPES.includes(file.type)) {
      showError(t('userDetails.invalidFileType', 'Please upload a JPG or PNG file.'));
      if (fileInputRef.current) { fileInputRef.current.value = ''; }
      return;
    }

    if (file.size > PHOTO_MAX_SIZE_BYTES) {
      showError(t('userDetails.fileTooLarge', 'File size must be less than 1MB.'));
      if (fileInputRef.current) { fileInputRef.current.value = ''; }
      return;
    }

    onPhotoSelect?.(file);
    if (fileInputRef.current) { fileInputRef.current.value = ''; }
  };

  return (
    <Card
      elevation={0}
      variant="outlined"
      data-testid={getTestId({ action: TestIdAction.VIEW, object: 'profile-header-card', page: 'account' })}
    >
      <Stack direction="row" alignItems="center" spacing={3} padding="24px 32px">
        <Avatar
          src={photoLoading ? undefined : (photoUrl ?? undefined)}
          alt={fullName}
        >
          {!photoUrl && !photoLoading && <Person />}
        </Avatar>

        <Stack flex={1} minWidth={0} spacing={0.5}>
          <Typography variant="h5" color="text.primary">{fullName}</Typography>
          {designation && (
            <Typography variant="body1" color="text.secondary">{designation}</Typography>
          )}
          {roleName && (
            <Chip label={roleName} size="small" color="default" variant="filled" />
          )}
        </Stack>

        {mode === 'edit' && (
          <Stack alignItems="flex-end" spacing={1} flexShrink={0}>
            <Stack direction="row" spacing={1}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                hidden
                onChange={handleFileChange}
                data-testid={getTestId({ action: TestIdAction.UPLOAD_PHOTO, object: 'file-input', page: 'account' })}
              />
              {hasPhoto && (
                <Button
                  variant="outlined"
                  size="medium"
                  color="error"
                  onClick={() => onPhotoRemove?.()}
                  data-testid={getTestId({ action: TestIdAction.REMOVE_PHOTO, object: 'button', page: 'account' })}
                >
                  {t('userDetails.removePhoto', 'Remove Photo')}
                </Button>
              )}
              <Button
                variant="outlined"
                size="medium"
                color="primary"
                onClick={handleUploadClick}
                data-testid={getTestId({ action: TestIdAction.UPLOAD_PHOTO, object: 'button', page: 'account' })}
              >
                {hasPhoto
                  ? t('userDetails.updatePhoto', 'Update Photo')
                  : t('userDetails.uploadPhoto', 'Upload Photo')}
              </Button>
            </Stack>
            <Typography variant="caption" color="text.disabled">
              {t('userDetails.photoConstraints', 'JPG or PNG. Max 1MB.')}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
